import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const SiteContext = createContext(null);
const siteKey = (memberId) => `greentek-selected-site:${memberId || "current"}`;
const supportScopeKey = (memberId) => `greentek-support-scope:${memberId || "current"}`;

export function SiteProvider({ memberId, enabled = true, children }) {
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [supportScope, setSupportScopeState] = useState(() => {
    try {
      return localStorage.getItem(supportScopeKey(memberId)) === "general" ? "general" : "site";
    } catch {
      return "site";
    }
  });
  const sitesQuery = useQuery({
    queryKey: ["sites", memberId],
    queryFn: async () => (await api.get("/sites")).data,
    enabled,
    staleTime: 60_000,
  });
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  useEffect(() => {
    if (!sitesQuery.isSuccess) {
      setSelectedSiteId(null);
      setSupportScopeState("site");
      return;
    }
    if (!sites.length) {
      setSelectedSiteId(null);
      setSupportScopeState("general");
      try {
        localStorage.removeItem(siteKey(memberId));
        localStorage.removeItem(supportScopeKey(memberId));
      } catch {}
      return;
    }
    let stored = null;
    let storedScope = null;
    try {
      stored = localStorage.getItem(siteKey(memberId));
      storedScope = localStorage.getItem(supportScopeKey(memberId));
    } catch {}
    const valid = sites.some((site) => site.id === stored);
    const next = valid ? stored : sites[0].id;
    const nextScope = storedScope === "general" ? "general" : "site";
    setSelectedSiteId(next);
    setSupportScopeState(nextScope);
    try {
      if (stored !== next) localStorage.setItem(siteKey(memberId), next);
      if (storedScope !== nextScope) localStorage.setItem(supportScopeKey(memberId), nextScope);
    } catch {}
  }, [memberId, sites, sitesQuery.isSuccess]);

  const setSite = useCallback((id) => {
    if (!sites.some((site) => site.id === id)) return;
    setSelectedSiteId(id);
    try {
      localStorage.setItem(siteKey(memberId), id);
    } catch {}
  }, [memberId, sites]);
  const setSupportScope = useCallback((scope) => {
    if (!sites.length) {
      setSupportScopeState("general");
      try {
        localStorage.removeItem(supportScopeKey(memberId));
      } catch {}
      return;
    }
    const next = scope === "general" ? "general" : "site";
    setSupportScopeState(next);
    try {
      localStorage.setItem(supportScopeKey(memberId), next);
    } catch {}
  }, [memberId, sites.length]);
  const value = useMemo(() => ({
    sites,
    selectedSite: sites.find((site) => site.id === selectedSiteId) ?? null,
    selectedSiteId,
    setSelectedSiteId: setSite,
    supportScope,
    setSupportScope,
    isLoading: sitesQuery.isLoading,
    error: sitesQuery.error,
    refetch: sitesQuery.refetch,
  }), [sites, selectedSiteId, setSite, supportScope, setSupportScope, sitesQuery.isLoading, sitesQuery.error, sitesQuery.refetch]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const value = useContext(SiteContext);
  if (!value) throw new Error("useSite must be used within SiteProvider");
  return value;
}
